import type { PaymentOrderResult } from './registration-api';

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => { open: () => void };
  }
}

export function loadRazorpayScript(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load payment checkout'));
    document.body.appendChild(script);
  });
}

export function buildRazorpayCheckoutOptions(input: {
  order: PaymentOrderResult;
  description: string;
  onSuccess: () => void;
  onDismiss?: () => void;
}): Record<string, unknown> {
  const options: Record<string, unknown> = {
    key: input.order.checkout.keyId,
    order_id: input.order.checkout.orderId,
    name: 'Learn Plus',
    description: input.description,
    handler: input.onSuccess,
    modal: {
      ondismiss: input.onDismiss,
    },
  };

  const prefill = input.order.prefill;
  if (prefill && (prefill.email || prefill.contact)) {
    options.prefill = {
      ...(prefill.name ? { name: prefill.name } : {}),
      ...(prefill.email ? { email: prefill.email } : {}),
      ...(prefill.contact ? { contact: prefill.contact } : {}),
    };
    options.readonly = {
      email: Boolean(prefill.email),
      contact: Boolean(prefill.contact),
    };
  }

  return options;
}

export async function openRazorpayCheckout(input: {
  order: PaymentOrderResult;
  description: string;
  onSuccess: () => void;
  onDismiss?: () => void;
}): Promise<void> {
  await loadRazorpayScript();
  if (!window.Razorpay) throw new Error('Payment checkout unavailable');
  const checkout = new window.Razorpay(buildRazorpayCheckoutOptions(input));
  checkout.open();
}
